import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/date-utils";
import type { BalanceBillItemResponseData } from "@/model/balance-bill";
import { DialogPortal } from "@radix-ui/react-dialog";
import { useCallback } from "react";
import ImagePreviewCarousel from "./balance-bill-image-preview-carousel";
import { EMPTY_VALUE } from "@/constants";
import { SomethingWentWrong } from "../empty";

type BalanceBillDocumentPreviewProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billDetails: BalanceBillItemResponseData | undefined;
};

const BalanceBillDocumentPreviewDialog: React.FC<
  BalanceBillDocumentPreviewProps
> = ({ open, onOpenChange, billDetails }) => {
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      onOpenChange(newOpen);
    },
    [onOpenChange],
  );

  const splitedDate = billDetails?.submittedAt?.split(",");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        {billDetails && Object.keys(billDetails).length > 0 ? (
          <DialogContent className="flex h-3/4 max-w-[45rem] flex-col p-0">
            <DialogHeader className="border-b px-6 py-4">
              <DialogTitle className="mt-0.5 text-base font-medium">
                {Boolean(billDetails) && billDetails?.ticketId ? (
                  <span>
                    {formatDate(splitedDate?.[0], "long", "en-GB")},{" "}
                    {splitedDate?.[1]}
                  </span>
                ) : (
                  EMPTY_VALUE
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="flex h-full flex-1 flex-col justify-start gap-6 overflow-y-auto p-5">
              {billDetails?.attachments &&
              billDetails?.attachments.length > 0 &&
              billDetails?.ticketId ? (
                <ImagePreviewCarousel
                  attachments={billDetails.attachments}
                  ticketId={billDetails.ticketId}
                />
              ) : (
                <div className="flex h-[500px] w-full items-center justify-center border border-border bg-muted">
                  <p className="text-gray-500">No attachments available</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-[0.9rem]">
                <p className="flex gap-3">
                  Zendesk Ticket:{" "}
                  <span className="font-medium">
                    {billDetails?.ticketId ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Claim Number:{" "}
                  <span className="font-medium">
                    {billDetails?.claimNumber ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Balance Bill Amount:{" "}
                  <span className="font-medium">
                    {billDetails?.balanceBillAmount ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Amount Billed:{" "}
                  <span className="font-medium">
                    {billDetails?.amountBilled ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Patient Name:{" "}
                  <span className="font-medium">
                    {billDetails?.patientName ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Date of Service:{" "}
                  <span className="font-medium">
                    {billDetails?.dateOfService ?? EMPTY_VALUE}
                  </span>
                </p>
                <p className="flex gap-3">
                  Provider:{" "}
                  <span className="font-medium">
                    {billDetails?.providerName ?? EMPTY_VALUE}
                  </span>
                </p>
              </div>
            </div>
          </DialogContent>
        ) : (
          <DialogContent className="flex h-1/3 items-center justify-center p-0">
            <SomethingWentWrong />
          </DialogContent>
        )}
      </DialogPortal>
    </Dialog>
  );
};

export default BalanceBillDocumentPreviewDialog;
